import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Tooltip from "@material-ui/core/Tooltip";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { EmptyHint, LoadingHint, tNegocio } from "./shared";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import PublicidadPayDialog from "./PublicidadPayDialog";
import PublicidadPaymentsReview from "./PublicidadPaymentsReview";
import { PaymentStatusChip } from "./PublicidadPaymentStatus";

const t = (key) => tNegocio(`pages.publicidad.plans.${key}`);
const useStyles = makeStyles((theme) => ({
  banner: {
    padding: 12,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#f8fafc",
    marginBottom: 14,
    fontSize: 13,
    lineHeight: 1.45,
  },
  warn: {
    borderColor: "rgba(180,83,9,0.35)",
    background: "rgba(180,83,9,0.08)",
  },
  ok: {
    borderColor: "rgba(4,120,87,0.35)",
    background: "rgba(4,120,87,0.08)",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
    gap: 10,
    marginBottom: 16,
  },
  plan: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: 12,
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minHeight: 100,
  },
  planOn: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
  },
  planName: { fontWeight: 800, fontSize: 15 },
  planPrice: { fontWeight: 800, color: "#047857", marginTop: 2 },
  planMeta: { fontSize: 12, color: theme.palette.text.secondary },
  features: {
    margin: "4px 0 0",
    padding: "0 0 0 16px",
    fontSize: 12,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  formHint: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginBottom: 8,
  },
  form: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 10,
    marginBottom: 12,
  },
  actions: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 },
  tableWrap: { overflowX: "auto" },
}));

const emptyForm = () => ({ planId: "", email: "", whatsapp: "", nombre: "" });

const isActiveCliente = (cliente) => {
  if (!cliente) return false;
  if (cliente.plan_activo === true || cliente.plan_activo === 1) return true;
  const estado = String(cliente.estado || "").toLowerCase();
  return estado === "activo" || estado === "active" || estado === "vigente";
};

const normalizeCliente = (payload) => {
  const cliente = payload?.cliente || payload || null;
  if (!cliente || typeof cliente !== "object") return null;
  return cliente;
};

export default function PublicidadPlanes() {
  const classes = useStyles();
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [cliente, setCliente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [payOrder, setPayOrder] = useState(null);
  const autofilled = useRef(false);
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const isPlatformAdmin = !!user?.super;
  const companyId = user?.companyId;

  const applyEmpresa = (empresa) => {
    if (!empresa || autofilled.current) return;
    autofilled.current = true;
    setForm((current) => ({
      ...current,
      email: current.email || empresa.email || "",
      whatsapp: current.whatsapp || empresa.whatsapp || "",
      nombre: current.nombre || empresa.nombre || "",
    }));
  };

  const applyLookup = (payload, { silent } = {}) => {
    const next = normalizeCliente(payload);
    setCliente(next);
    if (!next) return;
    setForm((current) => ({
      ...current,
      email: current.email || next.email || "",
      whatsapp: current.whatsapp || String(next.whatsapp || "").replace(/\D/g, ""),
      nombre: current.nombre || next.nombre || next.name || "",
      planId: current.planId || next.plan || next.plan_id || "",
    }));
    if (silent) return;
    if (next.existe === false) toast.info(t("lookupMissing"));
    else if (isActiveCliente(next)) toast.success(t("lookupActive"));
    else toast.info(t("lookupExpired"));
  };

  const lookup = async (email, whatsapp, options = {}) => {
    if (!email && !whatsapp) return null;
    const { data } = await api.post("/negocio/publicidad/partner/lookup", { email, whatsapp });
    applyLookup(data, options);
    return data;
  };

  // soft: refresca sin desmontar la página (y sin cerrar el diálogo de pago abierto).
  const reload = async ({ soft = false } = {}) => {
    if (!soft) setLoading(true);
    try {
      const [{ data: st }, ordersRes] = await Promise.all([
        api.get("/negocio/publicidad/partner/status"),
        api.get("/negocio/publicidad/partner/orders", { params: { limit: 40 } }),
      ]);
      setStatus(st);
      setOrders(Array.isArray(ordersRes.data?.records) ? ordersRes.data.records : []);
      applyEmpresa(st?.empresa);
      if (st?.configured) {
        const { data } = await api.get("/negocio/publicidad/partner/plans");
        const list = data?.plans || data?.planes || data?.items || data?.data || [];
        setPlans(Array.isArray(list) ? list : Array.isArray(data) ? data : []);
        const email = st?.empresa?.email || "";
        const whatsapp = st?.empresa?.whatsapp || "";
        if (email || whatsapp) {
          try {
            await lookup(email, whatsapp, { silent: true });
          } catch {
            /* lookup opcional al cargar */
          }
        }
      } else {
        setPlans([]);
      }
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  // El backend emite al marcar pagada / enviar comprobante; evita esperar el poll de 10s.
  useEffect(() => {
    if (!companyId) return undefined;
    const socket = socketManager?.getSocket?.(companyId);
    const event = `company-${companyId}-publicidad-order`;
    const onOrder = () => reload({ soft: true });
    socket?.on(event, onOrder);
    return () => { socket?.off(event, onOrder); };
  }, [companyId, socketManager]);

  const setField = (key) => (event) => {
    const value = event.target.value;
    setForm((current) => ({ ...current, [key]: value }));
    if (key === "email" || key === "whatsapp") setCliente(null);
  };

  const run = async (work) => {
    setBusy(true);
    try {
      await work();
    } catch (error) {
      toastError(error);
    } finally {
      setBusy(false);
    }
  };

  const buyable = useMemo(() => {
    const allowed = cliente?.planes_comprables;
    if (!Array.isArray(allowed) || !allowed.length) return null;
    return new Set(allowed.map(String));
  }, [cliente]);

  const planList = plans.map((plan) => ({
    id: plan.id || plan.plan_id,
    nombre: plan.nombre || plan.name || plan.id,
    precio: plan.precio_usd || plan.precio || plan.price,
    dias: plan.duracion_dias || plan.duration_days || 30,
    creditos: plan.creditos_mensuales || plan.credits || null,
    features: Array.isArray(plan.features) ? plan.features : [],
  })).filter((plan) => plan.id && (!buyable || buyable.has(String(plan.id))));

  const customerBanner = () => {
    if (!cliente) return null;
    if (cliente.existe === false) {
      return <div className={classes.banner}>{t("lookupMissing")}</div>;
    }
    const estado = isActiveCliente(cliente)
      ? t("customerActive")
      : (cliente.estado || t("customerInactive"));
    return (
      <div className={`${classes.banner}${isActiveCliente(cliente) ? ` ${classes.ok}` : ""}`}>
        {t("customerLine")
          .replace("{plan}", cliente.plan || cliente.plan_id || "—")
          .replace("{vence}", cliente.vence || "—")
          .replace("{estado}", estado)}
        {isActiveCliente(cliente) ? (
          <div style={{ marginTop: 6 }}>{t("lookupActive")}</div>
        ) : (
          <div style={{ marginTop: 6 }}>{t("lookupExpired")}</div>
        )}
      </div>
    );
  };

  if (loading) return <LoadingHint />;

  return (
    <div>
      {!status?.configured ? (
        <div className={`${classes.banner} ${classes.warn}`}>{t("needSetup")}</div>
      ) : null}

      {!status?.configured ? (
        <EmptyHint title={t("blockedTitle")} subtitle={t("blockedHint")} />
      ) : (
        <>
          <div className={classes.grid}>
            {planList.map((plan) => (
              <div
                key={plan.id}
                className={`${classes.plan}${form.planId === plan.id ? ` ${classes.planOn}` : ""}`}
                onClick={() => setForm((current) => ({ ...current, planId: plan.id }))}
                role="presentation"
              >
                <div className={classes.planName}>{plan.nombre}</div>
                <div className={classes.planPrice}>USD {plan.precio}</div>
                <div className={classes.planMeta}>{t("days").replace("{n}", String(plan.dias))}</div>
                {plan.creditos ? (
                  <div className={classes.planMeta}>{t("credits").replace("{n}", String(plan.creditos))}</div>
                ) : null}
                {plan.features.length ? (
                  <ul className={classes.features}>
                    {plan.features.map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>

          <div className={classes.formHint}>{t("formHint")}</div>
          <div className={classes.form}>
            <TextField select label={t("plan")} variant="outlined" size="small" value={form.planId} onChange={setField("planId")}>
              {planList.map((plan) => (
                <MenuItem key={plan.id} value={plan.id}>{plan.nombre} · USD {plan.precio}</MenuItem>
              ))}
            </TextField>
            <TextField
              label={t("email")}
              variant="outlined"
              size="small"
              value={form.email}
              onChange={setField("email")}
              onBlur={() => {
                if (form.email || form.whatsapp) {
                  run(() => lookup(form.email, form.whatsapp));
                }
              }}
            />
            <TextField
              label={t("whatsapp")}
              variant="outlined"
              size="small"
              value={form.whatsapp}
              onChange={setField("whatsapp")}
              helperText={t("whatsappHint")}
              onBlur={() => {
                if (form.email || form.whatsapp) {
                  run(() => lookup(form.email, form.whatsapp));
                }
              }}
            />
            <TextField label={t("name")} variant="outlined" size="small" value={form.nombre} onChange={setField("nombre")} />
          </div>

          <div className={classes.actions}>
            <Tooltip title={t("lookupHint")}>
              <span>
                <Button
                  variant="outlined"
                  disabled={busy || (!form.email && !form.whatsapp)}
                  onClick={() => run(() => lookup(form.email, form.whatsapp))}
                >
                  {t("lookup")}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={t("createOrderHint")}>
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={busy || !form.planId || (!form.email && !form.whatsapp)}
                  onClick={() => run(async () => {
                    await lookup(form.email, form.whatsapp, { silent: true }).catch(() => null);
                    const { data } = await api.post("/negocio/publicidad/partner/orders", form);
                    toast.success(t("orderOk").replace("{monto}", data.order?.monto || data.monto || ""));
                    await reload();
                    const created = data.order || data;
                    const orderId = created.order_id || created.id;
                    if (orderId) setPayOrder({ orderId: String(orderId), planId: form.planId, monto: created.monto, moneda: created.moneda || "USD", estado: "pendiente" });
                  })}
                >
                  {t("createOrder")}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={t("openPanelHint")}>
              <span>
                <Button
                  variant="outlined"
                  color={isActiveCliente(cliente) ? "primary" : "default"}
                  disabled={busy || (!form.email && !form.whatsapp)}
                  onClick={() => run(async () => {
                    const { data } = await api.post("/negocio/publicidad/partner/sso-link", {
                      email: form.email,
                      whatsapp: form.whatsapp,
                      next: "/dashboard",
                    });
                    if (data.url) window.open(data.url, "_blank", "noopener,noreferrer");
                    else toast.error(data.error || t("ssoFail"));
                  })}
                >
                  {t("openPanel")}
                </Button>
              </span>
            </Tooltip>
          </div>

          {customerBanner()}

          {isPlatformAdmin ? <PublicidadPaymentsReview onChanged={() => reload({ soft: true })} /> : null}

          <div className={classes.tableWrap}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("colOrder")}</TableCell>
                  <TableCell>{t("colPlan")}</TableCell>
                  <TableCell>{t("colCustomer")}</TableCell>
                  <TableCell>{t("colAmount")}</TableCell>
                  <TableCell>{t("colStatus")}</TableCell>
                  <TableCell align="right">{t("colActions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.orderId}</TableCell>
                    <TableCell>{row.planId}</TableCell>
                    <TableCell>
                      <div>{row.nombre || "—"}</div>
                      <div style={{ fontSize: 12, opacity: 0.75 }}>{row.email || row.whatsapp}</div>
                    </TableCell>
                    <TableCell>{row.moneda} {row.monto}</TableCell>
                    <TableCell><PaymentStatusChip row={row} /></TableCell>
                    <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
                      {row.estado === "pendiente" ? (
                        <>
                          {!["review", "paid"].includes(row.paymentStatus) ? (
                            <Button size="small" color="primary" variant="contained" onClick={() => setPayOrder(row)}>{t("pay.payButton")}</Button>
                          ) : null}
                          {!["review", "paid"].includes(row.paymentStatus) ? <Button size="small" onClick={() => run(async () => {
                            await api.post(`/negocio/publicidad/partner/orders/${encodeURIComponent(row.orderId)}/cancel`);
                            toast.success(t("cancelled"));
                            await reload();
                          })}>{t("cancel")}</Button> : null}
                        </>
                      ) : (
                        <Button
                          size="small"
                          onClick={() => run(async () => {
                            const { data } = await api.post("/negocio/publicidad/partner/sso-link", {
                              email: row.email,
                              whatsapp: row.whatsapp,
                              next: "/dashboard",
                            });
                            if (data.url) window.open(data.url, "_blank", "noopener,noreferrer");
                            else toast.error(data.error || t("ssoFail"));
                          })}
                        >
                          {t("openPanel")}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!orders.length ? (
                  <TableRow>
                    <TableCell colSpan={6}>{t("noOrders")}</TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      <PublicidadPayDialog order={payOrder} onClose={() => setPayOrder(null)} onChanged={() => reload({ soft: true })} />
    </div>
  );
}
